import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';

export const usePaginatedProducts = () => {
  const { getAuthHeaders } = useAuth();
  
  // State management
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectedProductsData, setSelectedProductsData] = useState([]);
  const [availableProductsData, setAvailableProductsData] = useState([]);
  
  // Pagination state for selected products
  const [selectedProductsPagination, setSelectedProductsPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_count: 0,
    has_next: false,
    has_prev: false
  });
  
  // Pagination state for available products
  const [availableProductsPagination, setAvailableProductsPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_count: 0,
    has_next: false,
    has_prev: false
  });
  
  // Search and loading state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  
  // Refs for cleanup
  const searchTimeoutRef = useRef(null);
  
  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);
  
  // Reset pagination when search changes
  useEffect(() => {
    setSelectedProductsPagination(prev => ({ ...prev, current_page: 1 }));
    setAvailableProductsPagination(prev => ({ ...prev, current_page: 1 }));
  }, [debouncedSearchTerm]);
  
  // Fetch selected products
  const fetchSelectedProducts = useCallback(async (page = 1) => {
    if (selectedProducts.size === 0) {
      setSelectedProductsData([]);
      setSelectedProductsPagination({
        current_page: 1,
        total_pages: 0,
        total_count: 0,
        has_next: false,
        has_prev: false
      });
      return;
    }
    
    setLoadingSelected(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const selectedCodes = Array.from(selectedProducts).join(',');
      const params = new URLSearchParams({
        limit: '20',
        offset: String((page - 1) * 20),
        search: debouncedSearchTerm,
        selected_only: 'true',
        selected_codes: selectedCodes
      });
      
      const response = await fetch(`${API_BASE_URL}/api/v1/products/selector?${params}`, { 
        headers 
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedProductsData(data.products || []);
        setSelectedProductsPagination(data.pagination || {
          current_page: 1,
          total_pages: 0,
          total_count: 0,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error) {
      console.error('Error fetching selected products:', error);
    } finally {
      setLoadingSelected(false);
    }
  }, [selectedProducts, debouncedSearchTerm, getAuthHeaders]);
  
  // Fetch available products (excluding selected ones)
  const fetchAvailableProducts = useCallback(async (page = 1) => {
    setLoadingAvailable(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const params = new URLSearchParams({
        limit: '20',
        offset: String((page - 1) * 20),
        search: debouncedSearchTerm
      });
      
      const response = await fetch(`${API_BASE_URL}/api/v1/products/selector?${params}`, { 
        headers 
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter out already selected products
        const availableProducts = (data.products || []).filter(
          product => !selectedProducts.has(product.product_code)
        );
        
        setAvailableProductsData(availableProducts);
        setAvailableProductsPagination(data.pagination || {
          current_page: 1,
          total_pages: 0,
          total_count: 0,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error) {
      console.error('Error fetching available products:', error);
    } finally {
      setLoadingAvailable(false);
    }
  }, [selectedProducts, debouncedSearchTerm, getAuthHeaders]);
  
  // Initialize selected products from configuration
  const initializeSelectedProducts = useCallback((productCodes) => {
    const newSelectedProducts = new Set(productCodes || []);
    setSelectedProducts(newSelectedProducts);
  }, []);
  
  // Add product to selected
  const addProduct = useCallback((productCode) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      newSet.add(productCode);
      return newSet;
    });
  }, []);
  
  // Remove product from selected
  const removeProduct = useCallback((productCode) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      newSet.delete(productCode);
      return newSet;
    });
  }, []);
  
  // Clear all selected products
  const clearAllProducts = useCallback(() => {
    setSelectedProducts(new Set());
  }, []);
  
  // Select all products from current available page
  const selectAllFromCurrentPage = useCallback(() => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      availableProductsData.forEach(product => {
        newSet.add(product.product_code);
      });
      return newSet;
    });
  }, [availableProductsData]);
  
  // Load selected products page
  const loadSelectedProductsPage = useCallback((page) => {
    setSelectedProductsPagination(prev => ({ ...prev, current_page: page }));
    fetchSelectedProducts(page);
  }, [fetchSelectedProducts]);
  
  // Load available products page
  const loadAvailableProductsPage = useCallback((page) => {
    setAvailableProductsPagination(prev => ({ ...prev, current_page: page }));
    fetchAvailableProducts(page);
  }, [fetchAvailableProducts]);
  
  // Fetch data when dependencies change
  useEffect(() => {
    fetchSelectedProducts(selectedProductsPagination.current_page);
  }, [selectedProducts, debouncedSearchTerm]);
  
  useEffect(() => {
    fetchAvailableProducts(availableProductsPagination.current_page);
  }, [selectedProducts, debouncedSearchTerm]);
  
  return {
    // Data
    selectedProductsData,
    availableProductsData,
    selectedProducts: Array.from(selectedProducts),
    
    // Pagination
    selectedProductsPagination,
    availableProductsPagination,
    
    // Search
    searchTerm,
    setSearchTerm,
    
    // Loading states
    loadingSelected,
    loadingAvailable,
    
    // Actions
    initializeSelectedProducts,
    addProduct,
    removeProduct,
    clearAllProducts,
    selectAllFromCurrentPage,
    loadSelectedProductsPage,
    loadAvailableProductsPage,
    
    // Refresh functions
    refreshSelectedProducts: () => fetchSelectedProducts(selectedProductsPagination.current_page),
    refreshAvailableProducts: () => fetchAvailableProducts(availableProductsPagination.current_page)
  };
};
